/**
 * The Chinese lunisolar calendar, computed rather than tabulated, for the "calendrier
 * chinois" gender chart: it needs the mother's lunar age and the lunar month of
 * conception, for any birth year a mother may have.
 *
 * Months start on the day of the new moon in China (UTC+8). Month 11 always holds the
 * winter solstice; a year with 13 months between two solstice months repeats the first
 * month without a major solar term (a leap month). New moons follow Meeus, Astronomical
 * Algorithms ch. 49, and the Sun's longitude ch. 25: both are accurate to minutes, which
 * only matters for a new moon within minutes of Chinese midnight.
 */

const RAD = Math.PI / 180;
const CHINA_OFFSET_DAYS = 8 / 24;

const sin = (degrees: number) => Math.sin(degrees * RAD);

/** Julian day of a new moon, for lunation `k` counted from 6 January 2000. */
const newMoonJde = (k: number): number => {
  const t = k / 1236.85;
  const e = 1 - 0.002516 * t - 0.0000074 * t * t;
  const m = 2.5534 + 29.1053567 * k - 0.0000014 * t * t;
  const mp = 201.5643 + 385.81693528 * k + 0.0107582 * t * t;
  const f = 160.7108 + 390.67050284 * k - 0.0016118 * t * t;
  const omega = 124.7746 - 1.56375588 * k + 0.0020672 * t * t;
  return (
    2451550.09766 +
    29.530588861 * k +
    0.00015437 * t * t -
    0.4072 * sin(mp) +
    0.17241 * e * sin(m) +
    0.01608 * sin(2 * mp) +
    0.01039 * sin(2 * f) +
    0.00739 * e * sin(mp - m) -
    0.00514 * e * sin(mp + m) +
    0.00208 * e * e * sin(2 * m) -
    0.00111 * sin(mp - 2 * f) -
    0.00057 * sin(mp + 2 * f) +
    0.00056 * e * sin(2 * mp + m) -
    0.00042 * sin(3 * mp) +
    0.00042 * e * sin(m + 2 * f) +
    0.00038 * e * sin(m - 2 * f) -
    0.00024 * e * sin(2 * mp - m) -
    0.00017 * sin(omega) -
    0.00007 * sin(mp + 2 * m) +
    0.00004 * sin(2 * mp - 2 * f) +
    0.00004 * sin(3 * m) +
    0.00003 * sin(mp + m - 2 * f) +
    0.00003 * sin(2 * mp + 2 * f) -
    0.00003 * sin(mp + m + 2 * f) +
    0.00003 * sin(mp - m + 2 * f) -
    0.00002 * sin(mp - m - 2 * f) -
    0.00002 * sin(3 * mp + m) +
    0.00002 * sin(4 * mp)
  );
};

/** The Sun's apparent longitude, in degrees [0, 360). */
const sunLongitude = (jd: number): number => {
  const t = (jd - 2451545) / 36525;
  const l0 = 280.46646 + 36000.76983 * t + 0.0003032 * t * t;
  const m = 357.52911 + 35999.05029 * t - 0.0001537 * t * t;
  const c =
    (1.914602 - 0.004817 * t - 0.000014 * t * t) * sin(m) +
    (0.019993 - 0.000101 * t) * sin(2 * m) +
    0.000289 * sin(3 * m);
  const apparent = l0 + c - 0.00569 - 0.00478 * sin(125.04 - 1934.136 * t);
  return ((apparent % 360) + 360) % 360;
};

/** Days since the Unix epoch, for an ISO day, and back. */
const epochDay = (iso: string) => {
  const [year = 1970, month = 1, day = 1] = iso.split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
};
const JD_UNIX_EPOCH = 2440587.5;

/** The day (epoch days) in China on which a Julian day falls. */
const chinaDay = (jd: number) => Math.floor(jd - JD_UNIX_EPOCH + CHINA_OFFSET_DAYS);

/** The Sun's longitude at the start of a Chinese day. */
const longitudeAtChinaDay = (day: number) => sunLongitude(day + JD_UNIX_EPOCH - CHINA_OFFSET_DAYS);

/** The first day of the lunar month holding `day`: the last new moon on or before it. */
const monthStart = (day: number): { start: number; k: number } => {
  let k = Math.floor((day + JD_UNIX_EPOCH - 2451550.09766) / 29.530588861) + 1;
  while (chinaDay(newMoonJde(k)) > day) k -= 1;
  return { start: chinaDay(newMoonJde(k)), k };
};

/** The day of the winter solstice (270°) in China, in the given Gregorian year. */
const winterSolstice = (year: number): number => {
  let day = epochDay(`${year}-12-18`);
  while (longitudeAtChinaDay(day + 1) < 270 || longitudeAtChinaDay(day + 1) > 300) day += 1;
  return day;
};

/** Whether the month from `start` to `next` holds a major solar term (a multiple of 30°). */
const hasMajorTerm = (start: number, next: number) =>
  Math.floor(longitudeAtChinaDay(start) / 30) !== Math.floor(longitudeAtChinaDay(next) / 30);

type LunarMonth = { start: number; number: number; leap: boolean };

/** The months from the solstice month of `year - 1` to the one of `year`, numbered. */
const monthsOfSui = (year: number): LunarMonth[] => {
  const first = monthStart(winterSolstice(year - 1));
  const last = monthStart(winterSolstice(year));
  const starts: number[] = [];
  for (let k = first.k; k <= last.k + 1; k += 1) starts.push(chinaDay(newMoonJde(k)));
  const count = last.k - first.k;
  const months: LunarMonth[] = [];
  let number = 11;
  let leapTaken = count === 12;
  for (let index = 0; index < count; index += 1) {
    const start = starts[index] ?? 0;
    const next = starts[index + 1] ?? 0;
    const leap = !leapTaken && index > 0 && !hasMajorTerm(start, next);
    if (leap) leapTaken = true;
    else if (index > 0) number = (number % 12) + 1;
    months.push({ start, number, leap });
  }
  return months;
};

/** The lunar month holding an ISO day, and the lunar year it belongs to. */
export const lunarDate = (iso: string): { year: number; month: number; leap: boolean } => {
  const day = epochDay(iso);
  const gregorianYear = Number(iso.slice(0, 4));
  for (const year of [gregorianYear, gregorianYear + 1]) {
    const months = monthsOfSui(year);
    const found = months.findLast((month) => month.start <= day);
    const end = monthStart(winterSolstice(year)).start;
    if (!found || day >= end) continue;
    // Months 11 and 12 of a sui belong to the lunar year that began before them.
    const lunarYear = found.number >= 11 && months.indexOf(found) < 3 ? year - 1 : year;
    return { year: lunarYear, month: found.number, leap: found.leap };
  }
  throw new Error(`No lunar month for ${iso}`);
};

/** Chinese New Year of a Gregorian year, as an ISO day. */
export const chineseNewYear = (year: number): string => {
  const month = monthsOfSui(year).find((entry) => entry.number === 1 && !entry.leap);
  return new Date((month?.start ?? 0) * 86_400_000).toISOString().slice(0, 10);
};

/**
 * The traditional chart: rows are the mother's lunar age (18 to 45), columns the lunar
 * month of conception. G is a boy, F a girl. Checked against its known totals: 164 boys
 * and 172 girls.
 */
const CHART: Record<number, string> = {
  18: "FGFGGGGGGGGG",
  19: "GFGFFGGFGGFF",
  20: "FGFGGGGGGFGG",
  21: "GFFFFFFFFFFF",
  22: "FGGFGFFGFFFF",
  23: "GGFGGFGFGGGF",
  24: "GFGGFGGFFFFF",
  25: "FGFGFGFGGGGG",
  26: "GFGFFGFFGGGG",
  27: "FGFGFFGGGGFG",
  28: "GFGFFFGGGGFF",
  29: "FGFFGGFFFGGG",
  30: "GFFFFFFFFFGG",
  31: "GFGFFFFFFFFG",
  32: "GFGFFFFFFFFG",
  33: "FGFGFFFGFFFG",
  34: "GFGFFFFFFFGG",
  35: "GGFGFFFGFFGG",
  36: "FGGFGFFFGGGG",
  37: "GGFFGGFFFFGF",
  38: "FGGFGFGFGFGF",
  39: "GGFFGFFGFGFG",
  40: "FGGFGGFFGFGF",
  41: "GGFGFGGFFGFG",
  42: "FGGGGFGGFGFG",
  43: "GFFGGGGFFFFF",
  44: "GGFFGFGGFGFG",
  45: "FGFGFGFGGFGG",
};

export const CHART_AGES = { min: 18, max: 45 } as const;

export const chartCell = (lunarAge: number, lunarMonth: number): "boy" | "girl" | null => {
  const cell = CHART[lunarAge]?.[lunarMonth - 1];
  return cell === "G" ? "boy" : cell === "F" ? "girl" : null;
};

export type GenderPrediction = {
  lunarAge: number;
  lunarMonth: number;
  leapMonth: boolean;
  /** Null when the lunar age falls outside the chart's 18-45. */
  prediction: "boy" | "girl" | null;
};

/**
 * The mother's lunar age (xusui) is 1 at birth and gains a year at each Chinese New Year,
 * so it is the difference of lunar years plus one.
 */
export const predictGender = (motherBirth: string, conception: string): GenderPrediction => {
  const birth = lunarDate(motherBirth);
  const conceived = lunarDate(conception);
  const lunarAge = conceived.year - birth.year + 1;
  return {
    lunarAge,
    lunarMonth: conceived.month,
    leapMonth: conceived.leap,
    prediction: chartCell(lunarAge, conceived.month),
  };
};
